import { createReducer, on, Action } from '@ngrx/store';
import { EntityAdapter, createEntityAdapter } from '@ngrx/entity';

import * as FormularyActions from './formulary.actions';
import {
  FormularyEntity,
  ServiceError,
  DrugList,
  FormattedDrug,
  OtherFormularyInformation,
  FormularyDrugList,
} from './formulary.models';

export const FORMULARY_FEATURE_KEY = 'formulary';

export interface FormularyState {
  loaded: boolean;
  data?: FormattedDrug[];
  otherFormularyInfo?: OtherFormularyInformation;
  error?: ServiceError | boolean;
}

export interface FormularyPartialState {
  readonly [FORMULARY_FEATURE_KEY]: FormularyState;
}

export const formularyAdapter: EntityAdapter<FormularyEntity> =
  createEntityAdapter<FormularyEntity>();

export const formularyInitialState: FormularyState = {
  loaded: false,
};

const formularyReducer = createReducer(
  formularyInitialState,
  on(FormularyActions.loadFormulary, (state, { loadMore }) => ({
    ...state,
    loaded: false,
    data: loadMore ? state.data : [],
    error: undefined,
  })),

  on(
    FormularyActions.loadFormularySuccess,
    (state, { formulary, loadMore }) => ({
      ...state,
      data: formatDrugList(
        formulary.formularyDrugList.drugList,
        state,
        loadMore,
      ),
      otherFormularyInfo: parseOtherFormularyInfo(formulary.formularyDrugList),
      loaded: true,
    }),
  ),

  on(FormularyActions.loadFormularyFailure, (state, { error }) => ({
    ...state,
    error,
    loaded: true,
  })),
);

export function reducer(state: FormularyState | undefined, action: Action) {
  return formularyReducer(state, action);
}
function parseOtherFormularyInfo(formularyDrugList: FormularyDrugList) {
  return {
    dateUpdated: formularyDrugList.dateUpdated,
    createdDate: formularyDrugList.createdDate,
    scheduledUpdate: formularyDrugList.scheduledUpdate,
    mailSpecialtyDaySupply: formularyDrugList.mailSpecialtyDaySupply,
    pdlMessageCode: formularyDrugList.pdlMessageCode,
    priorAuth: formularyDrugList.priorAuth,
    retailSpecialtyDaySupply: formularyDrugList.retailSpecialtyDaySupply,
    stateCode: formularyDrugList.stateCode,
    stepTherapy: formularyDrugList.stepTherapy,
    migrated: formularyDrugList.migrated,
    totalCount: formularyDrugList.totalCount,
    currentLimit: formularyDrugList.currentLimit,
    pdfFileName: formularyDrugList.pdfFileName,
  };
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function formatDrugList(
  druglists: DrugList[],
  stateData: FormularyState,
  loadMore: boolean,
) {
  let formattedDrugList: FormattedDrug[] = [];
  druglists.forEach((rx) => {
    const drugName = rx.drugName;
    const therapeuticClass = rx.therapeuticClass;
    const index =
      formattedDrugList.length > 0
        ? formattedDrugList.findIndex(
            (item) =>
              item.drugName === drugName &&
              item.therapeuticClass === therapeuticClass,
          )
        : -1;

    if (index !== -1) {
      const drugObj = formattedDrugList.filter(
        (item) =>
          item.drugName === drugName &&
          item.therapeuticClass === therapeuticClass,
      );
      drugObj[0].strength = [...drugObj[0].strength, rx.strength];
      drugObj[0].tier = [...drugObj[0].tier, rx.tier];
      drugObj[0].notes = [...drugObj[0].notes, ...[rx.notes]] as string[];
      drugObj[0].dosageForm = [...drugObj[0].dosageForm, rx.dosageForm];
      if (rx.dosageFormDesc) {
        const dFormDesc = drugObj[0].dosageFormDesc || [];
        drugObj[0].dosageFormDesc = [...dFormDesc, rx.dosageFormDesc];
      }
      drugObj[0].mailSpecialtyDayLimit = rx.mailSpecialtyDayLimit;
      drugObj[0].retailSpecialtyDayLimit = rx.retailSpecialtyDayLimit;
      formattedDrugList.splice(index, 1);
      formattedDrugList = [...formattedDrugList, drugObj[0]];
    } else {
      const stgth: string[] = [];
      stgth.push(rx.strength);
      const tr: string[] = [];
      tr.push(rx.tier);
      const dForm: string[] = [];
      dForm.push(rx.dosageForm);
      const dFormDesc: string[] = [];
      if (rx.dosageFormDesc) {
        dFormDesc.push(rx.dosageFormDesc);
      }

      formattedDrugList.push({
        drugName: rx.drugName,
        dosageForm: dForm,
        dosageFormDesc: dFormDesc,
        strength: stgth,
        tier: tr,
        notes: [rx.notes],
        therapeuticClass: rx.therapeuticClass,
        therapeuticClassCode: rx.therapeuticClassCode,
        mailSpecialtyDayLimit: rx.mailSpecialtyDayLimit,
        retailSpecialtyDayLimit: rx.retailSpecialtyDayLimit,
      });
    }
  });

  let loadMoreList = formattedDrugList;
  if (loadMore && stateData.data !== undefined) {
    loadMoreList = [...stateData.data, ...formattedDrugList];
  }
  return loadMoreList;
}
